import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import YouTubeEmbed from '../components/YouTubeEmbed';
import backendUrl from '../BackendUrl';
import '../styles/Dev.css'; // Import the external stylesheet

// Component for individual Dev Log
const DevLogItem = ({ dev }) => {
  const [showDescription, setShowDescription] = useState(false);
  const toggleDescription = () => setShowDescription(prev => !prev);

  return (
    <div className="dev-log-item">
      <h2>{new Date(dev.date).toLocaleDateString()} - {dev.log}</h2>
      <div className="video-wrapper">
        <YouTubeEmbed url={dev.url}/>
      </div>
      <button onClick={toggleDescription} className="toggle-btn">
        {showDescription ? '▲' : '▼'}
      </button>
      {showDescription && (
        <p className="dev-description">{dev.description}</p>
      )}
    </div>
  );
};

const Dev = () => {
  const [devs, setDevs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    timeOfYear: '',
    subject: '',
  });

  const limit = 10;

  useEffect(() => {
    const fetchDevs = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/devs/get-dev`);
        const data = Array.isArray(response.data) ? response.data : response.data.devs || [];
        // Ensure only ready dev logs are shown on the public page
        setDevs(data.filter((d) => d.ready));
      } catch (error) {
        console.error('Error fetching dev logs:', error);
      }
    };

    fetchDevs();
  }, []);

  // Client-side filtering of the dev logs
  const filteredDevs = useMemo(() => {
    return devs
      .filter(dev => {
        if (searchTerm.trim()) {
          const term = searchTerm.toLowerCase();
          return (
            dev.log.toLowerCase().includes(term) ||
            dev.description.toLowerCase().includes(term)
          );
        }
        return true;
      })
      .filter(dev => {
        if (filter.timeOfYear) {
          const month = new Date(dev.date).getMonth() + 1;
          switch (filter.timeOfYear) {
            case 'spring':
              return month >= 3 && month <= 5;
            case 'summer':
              return month >= 6 && month <= 8;
            case 'fall':
              return month >= 9 && month <= 11;
            case 'winter':
              return month === 12 || month <= 2;
            default:
              return true;
          }
        }
        return true;
      })
      .filter(dev => {
        if (filter.subject) {
          return dev.subject && dev.subject.toLowerCase() === filter.subject.toLowerCase();
        }
        return true;
      });
  }, [devs, searchTerm, filter]);

  const totalPages = Math.ceil(filteredDevs.length / limit);
  const currentDevLogs = filteredDevs.slice((currentPage - 1) * limit, currentPage * limit);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="dev-logs-page">
      <main className="dev-main">
        {/* Search bar at the top */}
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search dev logs..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
        </div>

        {/* Filter panel just under the search bar */}
        <div className="filters">
          <h3>Filters</h3>
          <div className="filter-group">
            <label>
              Time of Year:
              <select name="timeOfYear" value={filter.timeOfYear} onChange={handleFilterChange}>
                <option value="">All</option>
                <option value="spring">Spring</option>
                <option value="summer">Summer</option>
                <option value="fall">Fall</option>
                <option value="winter">Winter</option>
              </select>
            </label>
          </div>
          <div className="filter-group">
            <label>
              Subject Matter:
              <select name="subject" value={filter.subject} onChange={handleFilterChange}>
                <option value="">All</option>
                <option value="frontend">Frontend</option>
                <option value="backend">Backend</option>
                <option value="devops">DevOps</option>
              </select>
            </label>
          </div>
        </div>

        {/* Dev logs list */}
        <div className="dev-logs-list">
          {currentDevLogs.length > 0 ? (
            currentDevLogs.map(dev => <DevLogItem key={dev._id} dev={dev} />)
          ) : (
            <p>No dev logs found.</p>
          )}
        </div>

        {/* Pagination controls */}
        <div className="pagination">
          <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
            Prev
          </button>
          {[...Array(totalPages).keys()].map((_, idx) => {
            const page = idx + 1;
            return (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`pagination-btn ${page === currentPage ? 'active' : ''}`}
              >
                {page}
              </button>
            );
          })}
          <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
            Next
          </button>
        </div>
      </main>
    </div>
  );
};

export default Dev;